import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  getAllBackupSources,
  createBackupSource,
  updateBackupSource,
  deleteBackupSource,
  getBackupLogs,
  triggerBackup,
  subscribeToBackupSources,
  subscribeToBackupLogs,
  validateGoogleSheetsUrl,
  getBackupStats
} from '../services/backup';
import './DataBackup.css';

function DataBackup() {
  const navigate = useNavigate();
  const { accountId } = useParams();
  const [sources, setSources] = useState([]);
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingSource, setEditingSource] = useState(null);
  const [selectedSource, setSelectedSource] = useState(null);
  const [backingUp, setBackingUp] = useState({});

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    type: 'googleSheets',
    sourceUrl: '',
    sheetName: '工作表1',
    targetCollection: 'timers',
    mode: 'scheduled',
    scheduleInterval: 30,
    triggerField: '',
    postBackupAction: 'keep'
  });

  useEffect(() => {
    loadData();

    // Subscribe to real-time updates
    const unsubscribeSources = subscribeToBackupSources((sourcesData) => {
      setSources(sourcesData);
    });

    const unsubscribeLogs = subscribeToBackupLogs(null, (logsData) => {
      setLogs(logsData.slice(0, 20));
    });

    return () => {
      unsubscribeSources();
      unsubscribeLogs();
    };
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [sourcesData, statsData] = await Promise.all([
        getAllBackupSources(),
        getBackupStats()
      ]);

      setSources(sourcesData);
      setStats(statsData);
    } catch (err) {
      console.error('Error loading data:', err);
      alert('載入失敗：' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateSource = async (e) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      alert('請輸入備份名稱');
      return;
    }

    if (formData.type === 'googleSheets' && !validateGoogleSheetsUrl(formData.sourceUrl)) {
      alert('請輸入有效的 Google Sheets URL');
      return;
    }

    try {
      await createBackupSource(formData);
      alert('備份來源已創建');
      resetForm();
      setShowCreateModal(false);
    } catch (err) {
      console.error('Error creating source:', err);
      alert('創建失敗：' + err.message);
    }
  };

  const handleUpdateSource = async (e) => {
    e.preventDefault();

    if (!editingSource) return;

    try {
      await updateBackupSource(editingSource.id, formData);
      alert('備份來源已更新');
      resetForm();
      setEditingSource(null);
    } catch (err) {
      console.error('Error updating source:', err);
      alert('更新失敗：' + err.message);
    }
  };

  const handleDeleteSource = async (sourceId, sourceName) => {
    if (!window.confirm(`確定要刪除備份來源「${sourceName}」嗎？`)) return;

    try {
      await deleteBackupSource(sourceId);
      alert('備份來源已刪除');
    } catch (err) {
      console.error('Error deleting source:', err);
      alert('刪除失敗：' + err.message);
    }
  };

  const handleEditSource = (source) => {
    setEditingSource(source);
    setFormData({
      name: source.name,
      type: source.type,
      sourceUrl: source.sourceUrl,
      sheetName: source.sheetName || '工作表1',
      targetCollection: source.targetCollection,
      mode: source.mode,
      scheduleInterval: source.scheduleInterval || 30,
      triggerField: source.triggerField || '',
      postBackupAction: source.postBackupAction
    });
  };

  const handleToggleSync = async (sourceId, currentStatus) => {
    try {
      await updateBackupSource(sourceId, { syncEnabled: !currentStatus });
    } catch (err) {
      console.error('Error toggling sync:', err);
      alert('更新失敗：' + err.message);
    }
  };

  const handleTriggerBackup = async (sourceId, sourceName) => {
    if (!window.confirm(`確定要立即執行「${sourceName}」的備份嗎？`)) return;

    try {
      setBackingUp({ ...backingUp, [sourceId]: true });
      const result = await triggerBackup(sourceId);
      alert(`備份已觸發！\n處理記錄數：${result.recordsSynced || 0}`);
    } catch (err) {
      console.error('Error triggering backup:', err);
      alert('備份失敗：' + err.message);
    } finally {
      setBackingUp({ ...backingUp, [sourceId]: false });
    }
  };

  const handleViewLogs = async (source) => {
    setSelectedSource(source);
    try {
      const sourceLogs = await getBackupLogs(source.id, 20);
      setLogs(sourceLogs);
    } catch (err) {
      console.error('Error loading logs:', err);
      alert('載入記錄失敗：' + err.message);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      type: 'googleSheets',
      sourceUrl: '',
      sheetName: '工作表1',
      targetCollection: 'timers',
      mode: 'scheduled',
      scheduleInterval: 30,
      triggerField: '',
      postBackupAction: 'keep'
    });
  };

  const handleCancelEdit = () => {
    setEditingSource(null);
    resetForm();
  };

  const formatLastSync = (lastSyncAt) => {
    if (!lastSyncAt) return '從未同步';

    const timestamp = lastSyncAt.toDate ? lastSyncAt.toDate() : new Date(lastSyncAt);
    const now = new Date();
    const diffMs = now - timestamp;
    const diffMins = Math.floor(diffMs / 60000);

    if (diffMins < 1) return '剛剛';
    if (diffMins < 60) return `${diffMins} 分鐘前`;
    if (diffMins < 1440) return `${Math.floor(diffMins / 60)} 小時前`;
    return timestamp.toLocaleString('zh-TW');
  };

  const formatDuration = (startedAt, completedAt) => {
    if (!startedAt || !completedAt) return '-';

    const start = startedAt.toDate ? startedAt.toDate() : new Date(startedAt);
    const end = completedAt.toDate ? completedAt.toDate() : new Date(completedAt);
    const diffMs = end - start;
    const diffSecs = Math.floor(diffMs / 1000);

    if (diffSecs < 60) return `${diffSecs} 秒`;
    return `${Math.floor(diffSecs / 60)} 分 ${diffSecs % 60} 秒`;
  };

  if (loading) {
    return (
      <div className="data-backup">
        <div className="loading">載入中...</div>
      </div>
    );
  }

  return (
    <div className="data-backup">
      <header className="page-header">
        <div className="header-left">
          <h1>資料備份管理</h1>
          <p>管理資料來源的備份與同步設定</p>
        </div>
        <div className="header-right">
          <button
            className="btn btn-primary"
            onClick={() => {
              resetForm();
              setShowCreateModal(true);
            }}
          >
            ➕ 新增備份來源
          </button>
          <button
            className="btn btn-secondary"
            onClick={() => navigate('/dashboard')}
          >
            返回儀表板
          </button>
        </div>
      </header>

      {/* Stats Cards */}
      {stats && (
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-value">{stats.totalSources}</div>
            <div className="stat-label">總備份來源</div>
          </div>
          <div className="stat-card active">
            <div className="stat-value">{stats.activeSources}</div>
            <div className="stat-label">啟用中</div>
          </div>
          <div className="stat-card success">
            <div className="stat-value">{stats.successRate}%</div>
            <div className="stat-label">成功率</div>
          </div>
        </div>
      )}

      {/* Sources List */}
      <div className="sources-section">
        <h2 className="section-title">備份來源</h2>
        {sources.length === 0 ? (
          <div className="empty-state">
            <p>尚無備份來源</p>
            <button
              className="btn btn-primary"
              onClick={() => {
                resetForm();
                setShowCreateModal(true);
              }}
            >
              創建第一個備份來源
            </button>
          </div>
        ) : (
          <div className="sources-grid">
            {sources.map((source) => (
              <div key={source.id} className="source-card">
                <div className="source-header">
                  <div>
                    <h3>{source.name}</h3>
                    <div className="source-type">
                      {source.type === 'googleSheets' ? 'Google Sheets' : source.type}
                    </div>
                  </div>
                  <span className={`status-badge ${source.syncEnabled ? 'active' : 'inactive'}`}>
                    {source.syncEnabled ? '啟用' : '停用'}
                  </span>
                </div>

                <div className="source-info">
                  <div className="info-row">
                    <span className="label">目標集合：</span>
                    <span className="value">{source.targetCollection}</span>
                  </div>
                  <div className="info-row">
                    <span className="label">備份模式：</span>
                    <span className="value">
                      {source.mode === 'scheduled' ? `定時 (${source.scheduleInterval}分)` : '欄位觸發'}
                    </span>
                  </div>
                  <div className="info-row">
                    <span className="label">後處理：</span>
                    <span className="value">
                      {source.postBackupAction === 'delete' ? '備份後刪除' : '備份後保留'}
                    </span>
                  </div>
                  <div className="info-row">
                    <span className="label">最後同步：</span>
                    <span className="value">{formatLastSync(source.lastSyncAt)}</span>
                  </div>
                </div>

                <div className="source-actions">
                  <button
                    className="btn btn-sm btn-success"
                    onClick={() => handleTriggerBackup(source.id, source.name)}
                    disabled={backingUp[source.id]}
                  >
                    {backingUp[source.id] ? '備份中...' : '⚡ 立即備份'}
                  </button>
                  <button
                    className="btn btn-sm btn-primary"
                    onClick={() => handleEditSource(source)}
                  >
                    ✏️ 編輯
                  </button>
                  <button
                    className="btn btn-sm btn-secondary"
                    onClick={() => handleViewLogs(source)}
                  >
                    📋 查看記錄
                  </button>
                  <button
                    className={`btn btn-sm ${source.syncEnabled ? 'btn-warning' : 'btn-info'}`}
                    onClick={() => handleToggleSync(source.id, source.syncEnabled)}
                  >
                    {source.syncEnabled ? '停用' : '啟用'}
                  </button>
                  <button
                    className="btn btn-sm btn-danger"
                    onClick={() => handleDeleteSource(source.id, source.name)}
                  >
                    🗑️ 刪除
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Logs Section */}
      <div className="logs-section">
        <h2 className="section-title">
          備份記錄
          {selectedSource && ` - ${selectedSource.name}`}
          {selectedSource && (
            <button
              className="btn btn-sm btn-secondary"
              onClick={() => {
                setSelectedSource(null);
                loadData();
              }}
              style={{ marginLeft: '12px' }}
            >
              查看全部
            </button>
          )}
        </h2>
        {logs.length === 0 ? (
          <div className="empty-logs">尚無備份記錄</div>
        ) : (
          <div className="logs-table-container">
            <table className="logs-table">
              <thead>
                <tr>
                  <th>來源名稱</th>
                  <th>狀態</th>
                  <th>處理記錄</th>
                  <th>新增/更新/刪除</th>
                  <th>開始時間</th>
                  <th>耗時</th>
                </tr>
              </thead>
              <tbody>
                {logs.map((log) => (
                  <tr key={log.id}>
                    <td>{log.sourceName}</td>
                    <td>
                      <span className={`log-status status-${log.status}`}>
                        {log.status === 'success' ? '成功' :
                         log.status === 'error' ? '失敗' :
                         log.status === 'partial' ? '部分成功' : '處理中'}
                      </span>
                    </td>
                    <td>{log.recordsProcessed || 0}</td>
                    <td>
                      <span className="records-summary">
                        {log.recordsAdded || 0} / {log.recordsUpdated || 0} / {log.recordsDeleted || 0}
                      </span>
                    </td>
                    <td>
                      {log.startedAt
                        ? (log.startedAt.toDate ? log.startedAt.toDate() : new Date(log.startedAt)).toLocaleString('zh-TW')
                        : '-'}
                    </td>
                    <td>{formatDuration(log.startedAt, log.completedAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Create Modal */}
      {showCreateModal && (
        <div className="modal-overlay" onClick={() => setShowCreateModal(false)}>
          <div className="modal-content backup-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>新增備份來源</h2>
              <button className="close-btn" onClick={() => setShowCreateModal(false)}>
                ✕
              </button>
            </div>

            <form onSubmit={handleCreateSource} className="backup-form">
              <div className="form-group">
                <label>備份名稱 *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="例如: 賽事資料備份"
                  required
                />
              </div>

              <div className="form-group">
                <label>來源類型</label>
                <select
                  value={formData.type}
                  onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                >
                  <option value="googleSheets">Google Sheets</option>
                  <option value="mongodb">MongoDB</option>
                  <option value="custom">自定義</option>
                </select>
              </div>

              {formData.type === 'googleSheets' && (
                <>
                  <div className="form-group">
                    <label>Google Sheets URL *</label>
                    <input
                      type="url"
                      value={formData.sourceUrl}
                      onChange={(e) => setFormData({ ...formData, sourceUrl: e.target.value })}
                      placeholder="https://docs.google.com/spreadsheets/d/..."
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label>工作表名稱</label>
                    <input
                      type="text"
                      value={formData.sheetName}
                      onChange={(e) => setFormData({ ...formData, sheetName: e.target.value })}
                      placeholder="工作表1"
                    />
                  </div>
                </>
              )}

              <div className="form-group">
                <label>目標 MongoDB 集合</label>
                <input
                  type="text"
                  value={formData.targetCollection}
                  onChange={(e) => setFormData({ ...formData, targetCollection: e.target.value })}
                  placeholder="例如: timers"
                />
              </div>

              <div className="form-group">
                <label>備份模式</label>
                <select
                  value={formData.mode}
                  onChange={(e) => setFormData({ ...formData, mode: e.target.value })}
                >
                  <option value="scheduled">定時備份</option>
                  <option value="fieldTrigger">欄位觸發</option>
                </select>
              </div>

              {formData.mode === 'scheduled' && (
                <div className="form-group">
                  <label>備份間隔（分鐘）</label>
                  <input
                    type="number"
                    value={formData.scheduleInterval}
                    onChange={(e) => setFormData({ ...formData, scheduleInterval: parseInt(e.target.value) })}
                    min="5"
                    max="1440"
                  />
                </div>
              )}

              {formData.mode === 'fieldTrigger' && (
                <div className="form-group">
                  <label>觸發欄位名稱</label>
                  <input
                    type="text"
                    value={formData.triggerField}
                    onChange={(e) => setFormData({ ...formData, triggerField: e.target.value })}
                    placeholder="例如: status"
                  />
                </div>
              )}

              <div className="form-group">
                <label>備份後處理</label>
                <select
                  value={formData.postBackupAction}
                  onChange={(e) => setFormData({ ...formData, postBackupAction: e.target.value })}
                >
                  <option value="keep">備份後保留</option>
                  <option value="delete">備份後刪除</option>
                </select>
              </div>

              <div className="form-actions">
                <button type="button" className="btn btn-secondary" onClick={() => setShowCreateModal(false)}>
                  取消
                </button>
                <button type="submit" className="btn btn-primary">
                  創建
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {editingSource && (
        <div className="modal-overlay" onClick={handleCancelEdit}>
          <div className="modal-content backup-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>編輯備份來源</h2>
              <button className="close-btn" onClick={handleCancelEdit}>
                ✕
              </button>
            </div>

            <form onSubmit={handleUpdateSource} className="backup-form">
              <div className="form-group">
                <label>備份名稱 *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                />
              </div>

              <div className="form-group">
                <label>來源類型</label>
                <select
                  value={formData.type}
                  onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                >
                  <option value="googleSheets">Google Sheets</option>
                  <option value="mongodb">MongoDB</option>
                  <option value="custom">自定義</option>
                </select>
              </div>

              {formData.type === 'googleSheets' && (
                <>
                  <div className="form-group">
                    <label>Google Sheets URL *</label>
                    <input
                      type="url"
                      value={formData.sourceUrl}
                      onChange={(e) => setFormData({ ...formData, sourceUrl: e.target.value })}
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label>工作表名稱</label>
                    <input
                      type="text"
                      value={formData.sheetName}
                      onChange={(e) => setFormData({ ...formData, sheetName: e.target.value })}
                    />
                  </div>
                </>
              )}

              <div className="form-group">
                <label>目標 MongoDB 集合</label>
                <input
                  type="text"
                  value={formData.targetCollection}
                  onChange={(e) => setFormData({ ...formData, targetCollection: e.target.value })}
                />
              </div>

              <div className="form-group">
                <label>備份模式</label>
                <select
                  value={formData.mode}
                  onChange={(e) => setFormData({ ...formData, mode: e.target.value })}
                >
                  <option value="scheduled">定時備份</option>
                  <option value="fieldTrigger">欄位觸發</option>
                </select>
              </div>

              {formData.mode === 'scheduled' && (
                <div className="form-group">
                  <label>備份間隔（分鐘）</label>
                  <input
                    type="number"
                    value={formData.scheduleInterval}
                    onChange={(e) => setFormData({ ...formData, scheduleInterval: parseInt(e.target.value) })}
                    min="5"
                    max="1440"
                  />
                </div>
              )}

              {formData.mode === 'fieldTrigger' && (
                <div className="form-group">
                  <label>觸發欄位名稱</label>
                  <input
                    type="text"
                    value={formData.triggerField}
                    onChange={(e) => setFormData({ ...formData, triggerField: e.target.value })}
                  />
                </div>
              )}

              <div className="form-group">
                <label>備份後處理</label>
                <select
                  value={formData.postBackupAction}
                  onChange={(e) => setFormData({ ...formData, postBackupAction: e.target.value })}
                >
                  <option value="keep">備份後保留</option>
                  <option value="delete">備份後刪除</option>
                </select>
              </div>

              <div className="form-actions">
                <button type="button" className="btn btn-secondary" onClick={handleCancelEdit}>
                  取消
                </button>
                <button type="submit" className="btn btn-primary">
                  更新
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default DataBackup;
